import { Card, CardContent, CardHeader, CardTitle } from "@/components/Card";
import { Badge } from "@/components/Badge";
import { HelpTooltip } from "@/components/HelpTooltip";
import { BarChart, TrendingUp, Users, Gift, Heart, Building2, Wallet, Clock } from "lucide-react";
import { cn } from "@/lib/utils";
import { router } from '@inertiajs/react';

export function ReportsTab({ available_reports = [] }) {
  const handleNavigateToReport = (url) => {
    router.visit(url);
  };

  // Reportes hardcodeados por ahora hasta que el backend los provea
  const reports = available_reports.length > 0 ? available_reports : [
    {
      id: "redemptions",
      name: "Reporte de Canjes",
      description: "Análisis completo de redemptions con métricas, tendencias y exportación.",
      helpText: "Monitorea el canje de puntos por recompensas, analiza tasa de cumplimiento, tiempo de procesamiento y preferencias de usuarios.",
      url: "/admin/reports/redemptions",
      icon: "BarChart",
      status: "available"
    },
    {
      id: "recognitions",
      name: "Reporte de Reconocimientos",
      description: "Análisis de puntos compartidos entre empleados, valores corporativos y participación.",
      helpText: "Identifica tendencias de reconocimientos, top performers, distribución de hashtags y usuarios que más encarnan cada valor corporativo.",
      url: "/admin/reports/recognitions",
      icon: "Heart",
      status: "available"
    },
    {
      id: "departments",
      name: "Reporte por Departamento",
      description: "Análisis de engagement por área organizacional con métricas de participación y trending hashtags.",
      helpText: "Compara performance entre departamentos, identifica equipos con alta/baja participación y analiza cultura de reconocimiento por área.",
      url: "/admin/reports/departments",
      icon: "Building2",
      status: "available"
    },
    {
      id: "user_balances",
      name: "Reporte de Balance Individual",
      description: "Detalle de puntos por usuario, top 20 mayores balances, usuarios inactivos y distribución.",
      helpText: "Monitorea saldos de puntos, identifica usuarios inactivos (90+ días) y analiza distribución de riqueza en el sistema.",
      url: "/admin/reports/user_balances",
      icon: "Wallet",
      status: "available"
    },
    {
      id: "financial_history",
      name: "Historial Financiero",
      description: "Timeline completo de movimientos de puntos: reconocimientos, canjes y ajustes administrativos.",
      helpText: "Timeline detallado de todos los movimientos de puntos en el sistema, útil para auditoría y resolución de discrepancias.",
      url: "/admin/reports/financial_history",
      icon: "Clock",
      status: "available"
    }
    // Futuros reportes:
    // {
    //   id: "rewards",
    //   name: "Reporte de Premios",
    //   description: "Análisis del catálogo de premios, popularidad y distribución por categorías.",
    //   url: "/admin/reports/rewards",
    //   icon: "Gift",
    //   status: "coming_soon"
    // },
    // {
    //   id: "users",
    //   name: "Reporte de Usuarios",
    //   description: "Análisis de usuarios activos, balances y comportamiento.",
    //   url: "/admin/reports/users",
    //   icon: "Users",
    //   status: "coming_soon"
    // }
  ];

  const getIcon = (iconName) => {
    const icons = {
      BarChart: BarChart,
      TrendingUp: TrendingUp,
      Users: Users,
      Gift: Gift,
      Heart: Heart,
      Building2: Building2,
      Wallet: Wallet,
      Clock: Clock
    };
    const IconComponent = icons[iconName] || BarChart;
    return <IconComponent className="w-8 h-8" />;
  };

  return (
    <Card className="border-t-4 border-t-purple-500 shadow-sm animate-in fade-in slide-in-from-bottom-2">
      <CardHeader>
        <CardTitle className="flex items-center text-xl">
          <BarChart className="w-5 h-5 mr-2 text-purple-500" />
          Reportes y Analíticas
        </CardTitle>
        <p className="text-sm text-gray-500">
          Accede a los reportes disponibles para analizar el rendimiento del sistema.
        </p>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {reports.map((report) => (
            <div
              key={report.id}
              onClick={() => report.status === "available" && handleNavigateToReport(report.url)}
              className={cn(
                "border rounded-xl p-6 flex flex-col gap-4 transition-all",
                report.status === "available"
                  ? "bg-white hover:shadow-lg hover:scale-105 cursor-pointer border-purple-100"
                  : "bg-gray-50 border-gray-200 opacity-60 cursor-not-allowed"
              )}
            >
              <div className="flex items-start justify-between">
                <div className={cn(
                  "w-14 h-14 rounded-lg flex items-center justify-center",
                  report.status === "available"
                    ? "bg-purple-100 text-purple-600"
                    : "bg-gray-200 text-gray-400"
                )}>
                  {getIcon(report.icon)}
                </div>
                <Badge
                  variant="outline"
                  className={cn(
                    "text-[10px]",
                    report.status === "available"
                      ? "bg-green-100 text-green-700 border-green-200"
                      : "bg-gray-200 text-gray-600 border-gray-300"
                  )}
                >
                  {report.status === "available" ? "Disponible" : "Próximamente"}
                </Badge>
              </div>

              <div>
                <h3 className="font-bold text-gray-800 text-lg mb-2 flex items-center gap-2">
                  {report.name}
                  {report.helpText && <HelpTooltip content={report.helpText} />}
                </h3>
                <p className="text-sm text-gray-600 leading-relaxed">
                  {report.description}
                </p>
              </div>

              {report.status === "available" && (
                <div className="mt-auto pt-4 border-t border-gray-100">
                  <span className="text-xs font-medium text-purple-600 flex items-center">
                    Ver reporte →
                  </span>
                </div>
              )}
            </div>
          ))}
        </div>

        {reports.length === 0 && (
          <div className="text-center py-12">
            <BarChart className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-400">No hay reportes disponibles en este momento.</p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
